import React, { forwardRef } from 'react';
import type { ForwardRefExoticComponent, PropsWithoutRef, RefAttributes } from 'react';
import { ErrorBoundary, ChartLoading } from '@ant-design/charts-util';
import useChart from '../../hooks/useChart';
import { Plots } from '../../core';
import type { CommonConfig, Chart } from '../../interface';

export const BaseChart: ForwardRefExoticComponent<PropsWithoutRef<CommonConfig> & RefAttributes<Chart>> = forwardRef<
  Chart,
  CommonConfig
>(({ chartType = 'Base', ...config }, ref) => {
  const {
    containerStyle = {
      height: 'inherit',
      flex: 1
    },
    containerAttributes = {},
    className,
    loading,
    loadingTemplate,
    errorTemplate,
    onReady,
    ...rest
  } = config;

  const { chart, container } = useChart<Chart, CommonConfig>(Plots[chartType], {
    ...rest,
    onReady: (chartInstance) => {
      if (ref) {
        if (typeof ref === 'function') {
          ref(chartInstance);
        } else {
          (ref as React.MutableRefObject<Chart>).current = chartInstance;
        }
      }
      onReady?.(chartInstance);
    },
  });

  return (
    <ErrorBoundary errorTemplate={errorTemplate}>
      {loading && <ChartLoading loadingTemplate={loadingTemplate} theme={config.theme} loading={loading} />}
      <div className={className} style={containerStyle} ref={container} {...containerAttributes} />
    </ErrorBoundary>
  );
});
